import type {
  LoanDataForScenario,
  BorrowerData,
  HouseholdData,
  ScenarioConfig,
  MonthlyAmortizationDetail,
  LoanResults,
  FilingStatus,
} from '@/types/loanCalculationTypes';
import type { LoanBorrowerGS } from '@/types/nsldsTypes';
import { PlanType, PLAN_CONFIGS, type PlanConfig, type PlanIdrConfig } from '@/types/planTypes';

import { getPovertyGuideline, calculateIcrFactor } from './incomeCalculations';
import { calculateFixedPayment, calculateStandardPayment } from './paymentHelpers';
import { calculateGraduatedFactor } from './graduatedFactors';
import { addMonths, formatDateYYYYMM } from './dateHelpers';

// Constants from calculationScenario.gs
const MIN_PAYMENT_FLOOR = 0.01;
const MIN_GRADUATED_PAYMENT = 50;
const MAX_SIMULATION_MONTHS = 360; // Max 30 years
const MONTHS_PER_YEAR = 12;

const MFS_ICR_FACTOR_METHOD: 'COMBINED' | 'INDIVIDUAL' = 'COMBINED';

/**
 * Calculate the term for Standard Repayment Plan based on total loan balance
 * Under $25,000: 10 years
 * $25,000 to $49,999: 15 years
 * $50,000 to $99,999: 20 years
 * $100,000 and above: 25 years
 * @param totalBalance - Total loan balance
 * @returns Term in years
 */
function calculateStandardRepaymentTerm(totalBalance: number): number {
  if (totalBalance < 25000) return 10;
  if (totalBalance < 50000) return 15;
  if (totalBalance < 100000) return 20;
  return 25;
}

/**
 * Calculate RAP (Repayment Assistance Plan) payment
 * Formula: Minimum is $10, base payment is (AGI / 12) * payment factor
 * Payment factor: 1% for $10k-20k AGI, 2% for $20k-30k, etc., up to max 10%
 * Subtract $50 for each dependent
 * @param annualGrossIncome - Annual Gross Income (AGI)
 * @param familySize - Total family size
 * @param isMarried - Whether the borrower is married
 * @returns Monthly RAP payment
 */
function calculateRapPayment(
  annualGrossIncome: number,
  familySize: number,
  isMarried: boolean
): number {
  const MIN_RAP_PAYMENT = 10;
  const monthlyGrossIncome = annualGrossIncome / 12;

  // Calculate payment factor: sliding scale starting at $10k
  // $10k-20k = 1%, $20k-30k = 2%, etc., up to max 10%
  let paymentFactor = 0;
  if (annualGrossIncome >= 10000) {
    const incomeAbove10k = annualGrossIncome - 10000;
    const bracket = Math.floor(incomeAbove10k / 10000);
    paymentFactor = Math.min(0.1, (bracket + 1) / 100); // 1% for first bracket, 2% for second, etc.
  }

  // Base payment calculation
  const basePayment = monthlyGrossIncome * paymentFactor;

  // Calculate dependents based on family size and marital status
  // If married, family size includes spouse, so dependents = familySize - 2
  // If single, dependents = familySize - 1
  const dependents = Math.max(0, familySize - (isMarried ? 2 : 1));

  // Subtract $50 for each dependent
  const paymentWithDependents = basePayment - dependents * 50;

  // Apply minimum payment floor
  return Math.max(MIN_RAP_PAYMENT, paymentWithDependents);
}

// Internal state type, aligned with LoanSimulationState in GS
interface LoanSimulationStateInternal {
  loanAwardId: string;
  borrower: LoanBorrowerGS; // 'Me' | 'My partner'
  originalPrincipal: number;
  principalAtIcrEntry: number;
  cumulativeIcrCapitalizedInterest: number;
  loanInterestRate: number; // Actual rate used (can be refi)
  degreeType: 'undergraduate' | 'graduate';
  planType: PlanType;
  planConfig: PlanConfig;
  isPslfVariant: boolean; // Added to resolve 'Cannot find name'
  effectiveMonthlyRate: number;
  forgivenessThresholdMonths: number;

  // Pre-calculated base payments
  baseFixedPayment?: number; // For FIXED plans (also used for refi)
  baseGraduatedPayment?: number; // For GRADUATED plans (standard payment over the full graduated term)
  base10YrStdPayment: number | null; // For IDR caps (PAYE/IBR)
  base12YrStdPayment: number | null; // For ICR alternative calculation

  // Mutable State
  currentPrincipal: number;
  currentNonCapitalizedInterest: number;
  qualifiedMonthsCount: number;
  isLoanActive: boolean;

  // Forgiveness/Payoff Tracking
  loanForgivenFlag: boolean;
  principalAtForgiveness: number;
  interestAtForgiveness: number;
  loanPaidOffFlag: boolean;
  payOffMonthIndex: number | null;
  forgivenessMonthIndex: number | null;

  monthlyDetails: MonthlyAmortizationDetail[];
}

/**
 * Helper function to calculate and allocate the IDR payment for a single borrower's loans
 * within a specific plan group for MFS/Single/HoH scenarios.
 * Modifies the `minimumMonthlyPayments` array directly.
 */
function calculateAndAllocateIdrPaymentForBorrower(
  borrowerLoanIndices: number[],
  planKey: PlanType,
  planConfig: PlanIdrConfig,
  currentAnnualFpl: number,
  annualIncome: number, // Individual income for discretionary calculation
  combinedAnnualIncome: number, // Combined income passed for potential ICR factor use
  loanStates: LoanSimulationStateInternal[],
  minimumMonthlyPayments: number[],
  yearIndex: number,
  inflationRate: number,
  filingStatus: FilingStatus,
  borrowerData?: BorrowerData // Added borrower data for RAP calculation
): void {
  const povertyLineFactor = planConfig.povertyLineFactor;
  const currentMonthlyFpl = currentAnnualFpl / MONTHS_PER_YEAR;
  let totalIdrPaymentForBorrower = 0;

  // Special handling for RAP plans
  if (planKey === PlanType.RAP || planKey === PlanType.RAP_PSLF) {
    // For RAP, we need to calculate based on AGI and family size
    // We'll use the combined income for MFJ, individual income for others
    const incomeToUse = filingStatus === 'MFJ' ? combinedAnnualIncome : annualIncome;

    // Get family size from borrower data, fallback to 1 if not available
    const familySize = borrowerData?.familySize || 1;
    const isMarried = filingStatus === 'MFJ';

    totalIdrPaymentForBorrower = calculateRapPayment(incomeToUse, familySize, isMarried);
  } else {
    // Standard IDR calculation for non-RAP plans
    if (povertyLineFactor === null) {
      throw new Error(`Poverty line factor cannot be null for plan ${planKey}`);
    }

    const povertyThresholdMonthly = currentMonthlyFpl * povertyLineFactor;
    const monthlyDiscretionaryIncome = Math.max(
      0,
      annualIncome / MONTHS_PER_YEAR - povertyThresholdMonthly
    );

    if (planKey === PlanType.SAVE || planKey === PlanType.SAVE_PSLF) {
      let totalUndergradBalance = 0;
      let totalGradBalance = 0;
      borrowerLoanIndices.forEach((k_save) => {
        const state_save = loanStates[k_save];
        const balance = state_save.currentPrincipal + state_save.currentNonCapitalizedInterest;
        if (state_save.degreeType === 'undergraduate') totalUndergradBalance += balance;
        else totalGradBalance += balance;
      });
      const totalSAVEBalance = totalUndergradBalance + totalGradBalance;

      if (totalSAVEBalance > MIN_PAYMENT_FLOOR) {
        const effectiveSaveFactor =
          (totalUndergradBalance / totalSAVEBalance) * 0.05 +
          (totalGradBalance / totalSAVEBalance) * 0.1;
        totalIdrPaymentForBorrower = Math.max(0, monthlyDiscretionaryIncome * effectiveSaveFactor);
      } else {
        totalIdrPaymentForBorrower = 0;
      }
    } else if (planKey === PlanType.ICR || planKey === PlanType.ICR_PSLF) {
      const icrIncomePayment = monthlyDiscretionaryIncome * 0.2;
      let icrAlternativePayment = Infinity;

      let incomeToUseForFactor: number;
      let statusToUseForFactorTable: FilingStatus | 'MFJ'; // 'MFJ' is used when MFS_ICR_FACTOR_METHOD is 'COMBINED'

      if (filingStatus === 'MFS' && MFS_ICR_FACTOR_METHOD === 'COMBINED') {
        incomeToUseForFactor = combinedAnnualIncome;
        statusToUseForFactorTable = 'MFJ';
      } else {
        incomeToUseForFactor = annualIncome;
        statusToUseForFactorTable = filingStatus;
      }

      let total12YrBaseForBorrower = 0;
      borrowerLoanIndices.forEach((k_icr) => {
        const basePart = loanStates[k_icr].base12YrStdPayment;
        if (basePart !== null) {
          total12YrBaseForBorrower += basePart;
        }
      });

      if (total12YrBaseForBorrower >= 0) {
        const icrFactor = calculateIcrFactor(
          incomeToUseForFactor,
          statusToUseForFactorTable,
          yearIndex,
          inflationRate
        );
        if (typeof icrFactor === 'number') {
          icrAlternativePayment = total12YrBaseForBorrower * icrFactor;
        }
      }
      totalIdrPaymentForBorrower = Math.max(0, Math.min(icrIncomePayment, icrAlternativePayment));
    } else {
      // PAYE, IBR types
      const idrPaymentFactor = planConfig.paymentFactor;
      if (idrPaymentFactor === null) {
        throw new Error(`Payment factor cannot be null for plan ${planKey}`);
      }

      const idrPayment_beforeCap = Math.max(0, monthlyDiscretionaryIncome * idrPaymentFactor);

      let total10YrCapForBorrower = 0;
      borrowerLoanIndices.forEach((k_cap) => {
        const capPart = loanStates[k_cap].base10YrStdPayment;
        if (capPart !== null) {
          total10YrCapForBorrower += capPart;
        }
      });

      const isCappedPlan = [
        PlanType.PAYE,
        PlanType.PAYE_PSLF,
        PlanType.NEW_IBR,
        PlanType.NEW_IBR_PSLF,
        PlanType.OLD_IBR,
        PlanType.OLD_IBR_PSLF,
      ].includes(planKey);

      if (total10YrCapForBorrower >= 0 && isCappedPlan) {
        totalIdrPaymentForBorrower = Math.min(idrPayment_beforeCap, total10YrCapForBorrower);
      } else {
        totalIdrPaymentForBorrower = idrPayment_beforeCap;
      }
      totalIdrPaymentForBorrower = Math.max(0, totalIdrPaymentForBorrower);
    }
  }

  // Allocation
  if (totalIdrPaymentForBorrower > MIN_PAYMENT_FLOOR) {
    let totalBalanceInGroup = 0;
    borrowerLoanIndices.forEach((k_bal) => {
      totalBalanceInGroup +=
        loanStates[k_bal].currentPrincipal + loanStates[k_bal].currentNonCapitalizedInterest;
    });
    if (totalBalanceInGroup > MIN_PAYMENT_FLOOR) {
      borrowerLoanIndices.forEach((k_alloc) => {
        const loanBalance =
          loanStates[k_alloc].currentPrincipal + loanStates[k_alloc].currentNonCapitalizedInterest;
        const allocShare = loanBalance > 0 ? loanBalance / totalBalanceInGroup : 0;
        minimumMonthlyPayments[k_alloc] = totalIdrPaymentForBorrower * allocShare;
      });
    } else {
      borrowerLoanIndices.forEach((k_zero) => {
        minimumMonthlyPayments[k_zero] = 0;
      });
    }
  } else {
    borrowerLoanIndices.forEach((k_zero) => {
      minimumMonthlyPayments[k_zero] = 0;
    });
  }
}

export function calculateLoanScenario(
  loansDataForScenario: LoanDataForScenario[],
  borrowersData: BorrowerData[],
  householdData: HouseholdData,
  scenarioConfig: ScenarioConfig
): LoanResults[] {
  const numLoans = loansDataForScenario.length;
  const { inflationRate, incomeGrowthRate, taxRate } = scenarioConfig;
  const { filingStatus } = householdData;
  const simStartDateString = scenarioConfig.simulationStartDate || new Date().toISOString();
  const simulationStartDate = new Date(simStartDateString);

  const borrowerMap: Partial<Record<LoanBorrowerGS, BorrowerData>> = {};
  borrowersData.forEach((bd) => {
    borrowerMap[bd.borrowerIdentifier] = bd;
  });

  const primaryBorrowerData = borrowerMap['Me'];

  if (!primaryBorrowerData || !primaryBorrowerData.state) {
    throw new Error("Primary borrower ('Me') data (state/familySize) is missing or invalid.");
  }
  const partnerBorrowerData = borrowerMap['My partner'];
  if (partnerBorrowerData && !partnerBorrowerData.state) {
    throw new Error('Partner data is present but missing valid state/familySize.');
  }

  // --- I. Initialization Phase ---
  const loanSimulationStates: LoanSimulationStateInternal[] = [];
  const planGroupIndices: Partial<Record<PlanType, number[]>> = {};
  const sharedTotalCaps_MFJ: Partial<Record<PlanType, number>> = {};
  const sharedTotalIcrBase_MFJ: Partial<Record<PlanType, number>> = {};

  const initialAnnualFplMe = getPovertyGuideline(
    primaryBorrowerData.state,
    primaryBorrowerData.familySize
  );
  if (initialAnnualFplMe === null) {
    throw new Error(
      `Could not get valid FPL for 'Me': state ${
        primaryBorrowerData.state
      } size ${primaryBorrowerData.familySize.toString()}`
    );
  }
  let currentAnnualFplMe = initialAnnualFplMe;

  let initialAnnualFplPartner: number | null = null;
  let currentAnnualFplPartner: number | null = null;
  if (partnerBorrowerData) {
    initialAnnualFplPartner = getPovertyGuideline(
      partnerBorrowerData.state,
      partnerBorrowerData.familySize
    );
    if (initialAnnualFplPartner === null) {
      throw new Error(
        `Could not get valid FPL for 'My partner': state ${
          partnerBorrowerData.state
        } size ${partnerBorrowerData.familySize.toString()}`
      );
    }
    currentAnnualFplPartner = initialAnnualFplPartner;
  }

  for (let k = 0; k < numLoans; k++) {
    const inputData = loansDataForScenario[k];
    const loanInput = inputData.loanInput;
    const planType = inputData.planType;
    const planConfig = PLAN_CONFIGS[planType];

    let actualLoanInterestRate = loanInput['Loan Interest Rate'];
    let termMonthsForFixedOrGrad = 0;

    if (planType === PlanType.PRIVATE_REFINANCE) {
      actualLoanInterestRate = scenarioConfig.refiInterestRate;
      termMonthsForFixedOrGrad = scenarioConfig.refiPeriodYears * MONTHS_PER_YEAR;
    } else if (planConfig.type === 'FIXED') {
      termMonthsForFixedOrGrad = planConfig.termYears * MONTHS_PER_YEAR;
    } else if (planConfig.type === 'GRADUATED') {
      termMonthsForFixedOrGrad = planConfig.termYears * MONTHS_PER_YEAR;
    }

    let effectiveMonthlyRate = 0;
    if (actualLoanInterestRate > 0) {
      const dailyRate = actualLoanInterestRate / 365.25;
      const avgDaysPerMonth = 365.25 / MONTHS_PER_YEAR;
      effectiveMonthlyRate =
        planConfig.type === 'IDR'
          ? dailyRate * avgDaysPerMonth // Simple interest for IDR
          : Math.pow(1 + dailyRate, avgDaysPerMonth) - 1; // Compounding for others
    }

    let forgivenessThresholdMonths = MAX_SIMULATION_MONTHS + 1; // Default high
    let qualifiedMonthsCount = 0;
    const isPslfVariantCurrentLoan = planType.toString().includes('_PSLF');

    if (planConfig.type === 'IDR') {
      const idrConfig = planConfig;
      qualifiedMonthsCount = isPslfVariantCurrentLoan
        ? loanInput['Loan PSLF Cumulative Matched Months']
        : loanInput.qualifiedPaymentMonths;

      if (isPslfVariantCurrentLoan) {
        forgivenessThresholdMonths = 120;
      } else if (planType === PlanType.SAVE) {
        const originalPrincipalSAVE = loanInput.originalPrincipal;
        let calculatedForgivenessYears = 25; // Default max for grad if principal high
        if (typeof originalPrincipalSAVE === 'number' && originalPrincipalSAVE >= 0) {
          const additionalYears = Math.max(0, Math.ceil((originalPrincipalSAVE - 12000) / 1000));
          calculatedForgivenessYears = 10 + additionalYears;
        }
        const maxForgivenessYearsCap = loanInput.degreeType === 'undergraduate' ? 20 : 25;
        forgivenessThresholdMonths =
          Math.min(calculatedForgivenessYears, maxForgivenessYearsCap) * MONTHS_PER_YEAR;
      } else if (idrConfig.forgivenessYears) {
        forgivenessThresholdMonths = idrConfig.forgivenessYears * MONTHS_PER_YEAR;
      }
    }

    let baseFixedPaymentForState: number | undefined = undefined;
    if (
      planConfig.type === 'FIXED' ||
      planType === PlanType.STANDARD_10_YEAR ||
      planType === PlanType.PRIVATE_REFINANCE ||
      planType === PlanType.STANDARD_REPAYMENT
    ) {
      let termForCalc: number;

      if (planType === PlanType.STANDARD_10_YEAR) {
        termForCalc = 10 * MONTHS_PER_YEAR;
      } else if (planType === PlanType.STANDARD_REPAYMENT) {
        // Calculate term based on starting principal
        const termYears = calculateStandardRepaymentTerm(inputData.startingPrincipal);
        termForCalc = termYears * MONTHS_PER_YEAR;
      } else if (planType === PlanType.PRIVATE_REFINANCE) {
        termForCalc = scenarioConfig.refiPeriodYears * MONTHS_PER_YEAR;
      } else {
        termForCalc = termMonthsForFixedOrGrad;
      }

      if (termForCalc <= 0)
        throw new Error(
          `Invalid term length (${termForCalc.toString()}) for fixed plan ${planType} loan ${
            loanInput['Loan Award ID']
          }`
        );
      const calculatedPayment: number | null = calculateFixedPayment(
        inputData.startingPrincipal,
        actualLoanInterestRate,
        termForCalc
      );
      if (calculatedPayment === null) {
        if (inputData.startingPrincipal <= MIN_PAYMENT_FLOOR) {
          baseFixedPaymentForState = 0;
        } else {
          throw new Error(`Failed base fixed payment calc for loan ${loanInput['Loan Award ID']}`);
        }
      } else {
        baseFixedPaymentForState = calculatedPayment;
      }
    }

    let baseGraduatedPaymentForState: number | undefined = undefined;
    if (planConfig.type === 'GRADUATED') {
      if (termMonthsForFixedOrGrad <= 0)
        throw new Error(
          `Invalid term length (${termMonthsForFixedOrGrad.toString()}) for graduated plan ${planType} loan ${
            loanInput['Loan Award ID']
          }`
        );
      const calculatedPayment: number | null = calculateFixedPayment(
        inputData.startingPrincipal,
        actualLoanInterestRate,
        termMonthsForFixedOrGrad
      );
      if (calculatedPayment === null) {
        if (inputData.startingPrincipal <= MIN_PAYMENT_FLOOR) {
          baseGraduatedPaymentForState = 0;
        } else {
          throw new Error(
            `Failed base graduated payment calc for loan ${loanInput['Loan Award ID']}`
          );
        }
      } else {
        baseGraduatedPaymentForState = calculatedPayment;
      }
    }

    let base10YrStdPayment: number | null = null;
    let base12YrStdPayment: number | null = null;
    if (inputData.startingPrincipal > MIN_PAYMENT_FLOOR) {
      base10YrStdPayment = calculateStandardPayment(
        inputData.startingPrincipal,
        loanInput['Loan Interest Rate'],
        120
      );
      base12YrStdPayment = calculateStandardPayment(
        inputData.startingPrincipal,
        loanInput['Loan Interest Rate'],
        144
      );
      if (base10YrStdPayment === null) base10YrStdPayment = null;
      if (base12YrStdPayment === null) base12YrStdPayment = null;
    } else {
      base10YrStdPayment = 0;
      base12YrStdPayment = 0;
    }

    const borrowerIdentifierInternal: LoanBorrowerGS =
      loanInput.Borrower === 'user' ? 'Me' : 'My partner';

    loanSimulationStates[k] = {
      loanAwardId: loanInput['Loan Award ID'],
      borrower: borrowerIdentifierInternal,
      originalPrincipal: loanInput.originalPrincipal,
      principalAtIcrEntry: inputData.startingPrincipal,
      cumulativeIcrCapitalizedInterest: 0,
      loanInterestRate: actualLoanInterestRate,
      degreeType: loanInput.degreeType,
      planType: planType,
      planConfig: planConfig,
      isPslfVariant: isPslfVariantCurrentLoan,
      effectiveMonthlyRate: effectiveMonthlyRate,
      forgivenessThresholdMonths: forgivenessThresholdMonths,
      baseFixedPayment: baseFixedPaymentForState,
      baseGraduatedPayment: baseGraduatedPaymentForState,
      base10YrStdPayment: base10YrStdPayment,
      base12YrStdPayment: base12YrStdPayment,
      currentPrincipal: inputData.startingPrincipal,
      currentNonCapitalizedInterest: inputData.startingNonCapInterest,
      qualifiedMonthsCount: qualifiedMonthsCount,
      isLoanActive: inputData.startingPrincipal > MIN_PAYMENT_FLOOR,
      loanForgivenFlag: false,
      principalAtForgiveness: 0,
      interestAtForgiveness: 0,
      loanPaidOffFlag: false,
      payOffMonthIndex: null,
      forgivenessMonthIndex: null,
      monthlyDetails: [],
    };

    if (!planGroupIndices[planType]) planGroupIndices[planType] = [];
    planGroupIndices[planType].push(k);
  }

  if (filingStatus === 'MFJ') {
    for (const planKeyStr in planGroupIndices) {
      const planKey = planKeyStr as PlanType;
      const indices_mfj = planGroupIndices[planKey];
      if (!indices_mfj) continue;
      const planConfig_mfj = PLAN_CONFIGS[planKey];
      if (planConfig_mfj.type !== 'IDR') continue;

      if (
        [
          PlanType.PAYE,
          PlanType.PAYE_PSLF,
          PlanType.NEW_IBR,
          PlanType.NEW_IBR_PSLF,
          PlanType.OLD_IBR,
          PlanType.OLD_IBR_PSLF,
        ].includes(planKey)
      ) {
        let capSum_mfj = 0;
        indices_mfj.forEach((k_mfj) => {
          const base10 = loanSimulationStates[k_mfj].base10YrStdPayment;
          if (base10 !== null) capSum_mfj += base10;
        });
        if (capSum_mfj >= 0) sharedTotalCaps_MFJ[planKey] = capSum_mfj;
      }
      if (planKey === PlanType.ICR || planKey === PlanType.ICR_PSLF) {
        let baseSum_mfj = 0;
        indices_mfj.forEach((k_mfj) => {
          const base12 = loanSimulationStates[k_mfj].base12YrStdPayment;
          if (base12 !== null) baseSum_mfj += base12;
        });
        if (baseSum_mfj >= 0) sharedTotalIcrBase_MFJ[planKey] = baseSum_mfj;
      }
    }
  }

  // --- II. Master Monthly Simulation Loop ---
  let monthIndex = 0;
  while (
    monthIndex < MAX_SIMULATION_MONTHS &&
    loanSimulationStates.some((state) => state.isLoanActive)
  ) {
    const yearIndex = Math.floor(monthIndex / MONTHS_PER_YEAR);

    if (monthIndex > 0 && monthIndex % MONTHS_PER_YEAR === 0) {
      currentAnnualFplMe *= 1 + inflationRate;
      if (currentAnnualFplPartner !== null) currentAnnualFplPartner *= 1 + inflationRate;

      // ICR Annual Capitalization - REVISED LOGIC
      const icrIndices = (planGroupIndices[PlanType.ICR] || []).concat(
        planGroupIndices[PlanType.ICR_PSLF] || []
      );
      icrIndices.forEach((k_icr_cap) => {
        const state = loanSimulationStates[k_icr_cap];
        if (!state.isLoanActive || state.currentNonCapitalizedInterest <= MIN_PAYMENT_FLOOR) {
          return;
        }

        const icrCapPercentage = 0.1;
        // Cap is 10% of the principal when the loan entered this ICR plan simulation segment
        const maxAllowedCumulativeCapitalization = state.principalAtIcrEntry * icrCapPercentage;

        // How much more interest *can* be capitalized in total for this loan under ICR
        const remainingAllowedToCapitalizeOverall = Math.max(
          0,
          maxAllowedCumulativeCapitalization - state.cumulativeIcrCapitalizedInterest
        );

        // Determine actual amount to capitalize this year
        let amountToCapitalizeThisYear = Math.min(
          state.currentNonCapitalizedInterest, // Available non-capitalized interest
          remainingAllowedToCapitalizeOverall // What's left under the 10% cumulative cap
        );
        amountToCapitalizeThisYear = Math.max(0, amountToCapitalizeThisYear); // Ensure it's not negative

        if (amountToCapitalizeThisYear > MIN_PAYMENT_FLOOR) {
          state.currentPrincipal += amountToCapitalizeThisYear;
          state.currentNonCapitalizedInterest -= amountToCapitalizeThisYear;
          state.cumulativeIcrCapitalizedInterest += amountToCapitalizeThisYear; // Track cumulative

          // Ensure non-capitalized interest doesn't go negative
          state.currentNonCapitalizedInterest = Math.max(0, state.currentNonCapitalizedInterest);
        }
      });
      // END REVISED ICR Annual Capitalization
    }

    const minimumMonthlyPayments: number[] = new Array<number>(numLoans).fill(0);

    const getCurrentYearIncome = (bd: BorrowerData | undefined, yIdx: number): number => {
      if (!bd || bd.futureAnnualGrossIncome.length === 0) return 0; // Check !bd OR length === 0
      const baseIncome = bd.futureAnnualGrossIncome[0];
      const income = baseIncome * Math.pow(1 + incomeGrowthRate, yIdx);
      return typeof income === 'number' && isFinite(income) ? income : 0;
    };
    const incomeMe = getCurrentYearIncome(primaryBorrowerData, yearIndex);
    const incomePartner = getCurrentYearIncome(partnerBorrowerData, yearIndex);
    const combinedIncome = incomeMe + incomePartner;

    for (const planKeyStr in planGroupIndices) {
      const planKey = planKeyStr as PlanType;
      const planConfig = PLAN_CONFIGS[planKey];
      const loanIndicesInGroup = planGroupIndices[planKey];
      if (!loanIndicesInGroup) continue;

      const activeLoanIndicesInGroup = loanIndicesInGroup.filter(
        (k_active) => loanSimulationStates[k_active].isLoanActive
      );
      if (activeLoanIndicesInGroup.length === 0) continue;

      if (planConfig.type === 'FIXED' || planConfig.type === 'GRADUATED') {
        activeLoanIndicesInGroup.forEach((k_fixed_grad) => {
          const state = loanSimulationStates[k_fixed_grad];
          let payment = 0;
          if (planConfig.type === 'FIXED') {
            payment = state.baseFixedPayment !== undefined ? state.baseFixedPayment : 0;
          } else {
            // GRADUATED
            const basePayment =
              state.baseGraduatedPayment !== undefined ? state.baseGraduatedPayment : 0;
            const gradConfig = planConfig;
            let factor: number | null = null;
            const gradTerm = gradConfig.termYears;
            if (gradTerm === 10 || gradTerm === 25) {
              factor = calculateGraduatedFactor(gradTerm, state.loanInterestRate, monthIndex);
            }
            if (typeof factor === 'number' && isFinite(factor)) {
              payment = basePayment * factor;
            } else {
              payment = basePayment; // Fallback if factor fails
            }
            const interestAccrued = state.currentPrincipal * state.effectiveMonthlyRate;
            payment = Math.max(payment, MIN_GRADUATED_PAYMENT, interestAccrued + MIN_PAYMENT_FLOOR);
          }
          minimumMonthlyPayments[k_fixed_grad] = Math.max(0, payment);
        });
        continue;
      }

      // If not FIXED or GRADUATED, it must be IDR because PRIVATE_REFINANCE is type FIXED
      // The linter indicated planConfig.type === 'IDR' is always true here.
      const idrConfig = planConfig; // Cast as PlanIdrConfig since type === 'IDR'
      const povertyLineFactor = idrConfig.povertyLineFactor;

      if (filingStatus === 'MFJ') {
        let totalIdrPaymentForGroupMFJ = 0;

        // Special handling for RAP plans
        if (planKey === PlanType.RAP || planKey === PlanType.RAP_PSLF) {
          // For RAP MFJ, use combined income and combined family size
          // Use primary borrower's family size as it should include the full household
          const familySize = primaryBorrowerData.familySize || 2; // Default to 2 for married couple
          totalIdrPaymentForGroupMFJ = calculateRapPayment(combinedIncome, familySize, true);
        } else {
          // Standard IDR calculation for non-RAP plans
          if (povertyLineFactor === null) {
            throw new Error(`Poverty line factor cannot be null for plan ${planKey}`);
          }

          const currentMonthlyFplMFJ = currentAnnualFplMe / MONTHS_PER_YEAR;
          const povertyThresholdMonthlyMFJ = currentMonthlyFplMFJ * povertyLineFactor;
          const monthlyDiscretionaryIncomeMFJ = Math.max(
            0,
            combinedIncome / MONTHS_PER_YEAR - povertyThresholdMonthlyMFJ
          );

          if (planKey === PlanType.SAVE || planKey === PlanType.SAVE_PSLF) {
            let totalUndergradBalance = 0,
              totalGradBalance = 0;
            activeLoanIndicesInGroup.forEach((k_save) => {
              const state_save = loanSimulationStates[k_save];
              const balance =
                state_save.currentPrincipal + state_save.currentNonCapitalizedInterest;
              if (state_save.degreeType === 'undergraduate') totalUndergradBalance += balance;
              else totalGradBalance += balance;
            });
            const totalSAVEBalance = totalUndergradBalance + totalGradBalance;
            if (totalSAVEBalance > MIN_PAYMENT_FLOOR) {
              const effectiveSaveFactor =
                (totalUndergradBalance / totalSAVEBalance) * 0.05 +
                (totalGradBalance / totalSAVEBalance) * 0.1;
              totalIdrPaymentForGroupMFJ = Math.max(
                0,
                monthlyDiscretionaryIncomeMFJ * effectiveSaveFactor
              );
            } else {
              totalIdrPaymentForGroupMFJ = 0;
            }
          } else if (planKey === PlanType.ICR || planKey === PlanType.ICR_PSLF) {
            const icrIncomePayment = monthlyDiscretionaryIncomeMFJ * 0.2;
            let icrAlternativePayment = Infinity;
            const basePaymentSum = sharedTotalIcrBase_MFJ[planKey];
            if (typeof basePaymentSum === 'number' && basePaymentSum >= 0) {
              const icrFactorVal = calculateIcrFactor(
                combinedIncome,
                filingStatus,
                yearIndex,
                inflationRate
              );
              if (typeof icrFactorVal === 'number' && isFinite(icrFactorVal)) {
                icrAlternativePayment = basePaymentSum * icrFactorVal;
              }
            }
            totalIdrPaymentForGroupMFJ = Math.max(
              0,
              Math.min(icrIncomePayment, icrAlternativePayment)
            );
          } else {
            // PAYE, IBR
            const idrPaymentFactor = idrConfig.paymentFactor;
            if (idrPaymentFactor === null) {
              throw new Error(`Payment factor cannot be null for plan ${planKey}`);
            }

            const idrPayment_beforeCap = Math.max(
              0,
              monthlyDiscretionaryIncomeMFJ * idrPaymentFactor
            );
            const capSum = sharedTotalCaps_MFJ[planKey];
            if (
              [
                PlanType.PAYE,
                PlanType.PAYE_PSLF,
                PlanType.NEW_IBR,
                PlanType.NEW_IBR_PSLF,
                PlanType.OLD_IBR,
                PlanType.OLD_IBR_PSLF,
              ].includes(planKey) &&
              typeof capSum === 'number' &&
              capSum >= 0
            ) {
              totalIdrPaymentForGroupMFJ = Math.min(idrPayment_beforeCap, capSum);
            } else {
              totalIdrPaymentForGroupMFJ = idrPayment_beforeCap;
            }
          }
        }

        totalIdrPaymentForGroupMFJ = Math.max(0, totalIdrPaymentForGroupMFJ);

        // MFJ Allocation
        if (totalIdrPaymentForGroupMFJ > MIN_PAYMENT_FLOOR) {
          let totalBalanceMe = 0,
            totalBalancePartner = 0;
          const meIndices_mfj_alloc: number[] = [],
            partnerIndices_mfj_alloc: number[] = [];
          activeLoanIndicesInGroup.forEach((k_alloc_mfj) => {
            const state_alloc_mfj = loanSimulationStates[k_alloc_mfj];
            const balance_alloc_mfj =
              state_alloc_mfj.currentPrincipal + state_alloc_mfj.currentNonCapitalizedInterest;
            if (state_alloc_mfj.borrower === 'Me') {
              totalBalanceMe += balance_alloc_mfj;
              meIndices_mfj_alloc.push(k_alloc_mfj);
            } else {
              totalBalancePartner += balance_alloc_mfj;
              partnerIndices_mfj_alloc.push(k_alloc_mfj);
            }
          });
          const totalGroupBalance = totalBalanceMe + totalBalancePartner;
          if (totalGroupBalance > MIN_PAYMENT_FLOOR) {
            const meShare = totalGroupBalance > 0 ? totalBalanceMe / totalGroupBalance : 0;
            const paymentForMe = totalIdrPaymentForGroupMFJ * meShare;
            const paymentForPartner = totalIdrPaymentForGroupMFJ * (1 - meShare);

            if (paymentForMe > 0 && totalBalanceMe > 0) {
              meIndices_mfj_alloc.forEach((k_me) => {
                const loanBalance =
                  loanSimulationStates[k_me].currentPrincipal +
                  loanSimulationStates[k_me].currentNonCapitalizedInterest;
                minimumMonthlyPayments[k_me] =
                  paymentForMe * (loanBalance > 0 ? loanBalance / totalBalanceMe : 0);
              });
            } else {
              meIndices_mfj_alloc.forEach((k_me) => {
                minimumMonthlyPayments[k_me] = 0;
              });
            }
            if (paymentForPartner > 0 && totalBalancePartner > 0) {
              partnerIndices_mfj_alloc.forEach((k_partner) => {
                const loanBalance =
                  loanSimulationStates[k_partner].currentPrincipal +
                  loanSimulationStates[k_partner].currentNonCapitalizedInterest;
                minimumMonthlyPayments[k_partner] =
                  paymentForPartner * (loanBalance > 0 ? loanBalance / totalBalancePartner : 0);
              });
            } else {
              partnerIndices_mfj_alloc.forEach((k_partner) => {
                minimumMonthlyPayments[k_partner] = 0;
              });
            }
          } else {
            activeLoanIndicesInGroup.forEach((k_zero) => {
              minimumMonthlyPayments[k_zero] = 0;
            });
          }
        } else {
          activeLoanIndicesInGroup.forEach((k_zero) => {
            minimumMonthlyPayments[k_zero] = 0;
          });
        }
      } else {
        // MFS / Single / HoH
        const meIndicesInGroup = activeLoanIndicesInGroup.filter(
          (k_sep) => loanSimulationStates[k_sep].borrower === 'Me'
        );
        const partnerIndicesInGroup = activeLoanIndicesInGroup.filter(
          (k_sep) => loanSimulationStates[k_sep].borrower === 'My partner'
        );

        if (meIndicesInGroup.length > 0) {
          calculateAndAllocateIdrPaymentForBorrower(
            meIndicesInGroup,
            planKey,
            idrConfig,
            currentAnnualFplMe,
            incomeMe,
            combinedIncome,
            loanSimulationStates,
            minimumMonthlyPayments,
            yearIndex,
            inflationRate,
            filingStatus,
            primaryBorrowerData
          );
        }
        if (
          partnerIndicesInGroup.length > 0 &&
          partnerBorrowerData &&
          currentAnnualFplPartner !== null
        ) {
          calculateAndAllocateIdrPaymentForBorrower(
            partnerIndicesInGroup,
            planKey,
            idrConfig,
            currentAnnualFplPartner,
            incomePartner,
            combinedIncome,
            loanSimulationStates,
            minimumMonthlyPayments,
            yearIndex,
            inflationRate,
            filingStatus,
            partnerBorrowerData
          );
        }
      }
    }

    // C. Apply Payments & Update State
    for (let k_apply = 0; k_apply < numLoans; k_apply++) {
      const state = loanSimulationStates[k_apply];
      if (!state.isLoanActive) continue;

      const startingPrincipalLoan = state.currentPrincipal;
      const startingNonCapitalizedInterest = state.currentNonCapitalizedInterest;
      const accruedInterestForMonth = startingPrincipalLoan * state.effectiveMonthlyRate;
      const paymentToApply = Math.max(0, minimumMonthlyPayments[k_apply]);

      const totalOwedThisMonthForLoan =
        startingPrincipalLoan + startingNonCapitalizedInterest + accruedInterestForMonth;
      const actualPayment = Math.min(paymentToApply, Math.max(0, totalOwedThisMonthForLoan));

      let interestPaid = 0;
      let principalPaid = 0;

      // Allocate payment: 1st to new interest, 2nd to existing non-cap interest, 3rd to principal
      const paymentTowardsNewInterest = Math.min(actualPayment, accruedInterestForMonth);
      interestPaid += paymentTowardsNewInterest;
      const remainingPaymentAfterNewInterest = actualPayment - paymentTowardsNewInterest;

      const paymentTowardsExistingInterest = Math.min(
        remainingPaymentAfterNewInterest,
        startingNonCapitalizedInterest
      );
      interestPaid += paymentTowardsExistingInterest;
      const remainingPaymentAfterOldInterest =
        remainingPaymentAfterNewInterest - paymentTowardsExistingInterest;

      principalPaid = Math.min(remainingPaymentAfterOldInterest, startingPrincipalLoan);

      // Calculate remaining interest components
      const unpaidAccruedInterest = accruedInterestForMonth - paymentTowardsNewInterest;
      const remainingNonCapInterestAfterPayment =
        startingNonCapitalizedInterest - paymentTowardsExistingInterest;

      let endingPrincipalLoan = startingPrincipalLoan - principalPaid;
      let endingNonCapInterestLoan = remainingNonCapInterestAfterPayment + unpaidAccruedInterest;

      if (state.planConfig.capitalizationStrategy === 'always' && !state.isPslfVariant) {
        if (endingNonCapInterestLoan > MIN_PAYMENT_FLOOR) {
          endingPrincipalLoan += endingNonCapInterestLoan;
          endingNonCapInterestLoan = 0;
        }
      } else if (state.planConfig.type === 'IDR' && state.planConfig.interestSubsidyApplies) {
        // For plans with interest subsidy (SAVE, RAP), balance should not grow due to unpaid interest
        endingNonCapInterestLoan = remainingNonCapInterestAfterPayment;
      }

      state.currentPrincipal = Math.max(0, endingPrincipalLoan);
      state.currentNonCapitalizedInterest = Math.max(0, endingNonCapInterestLoan);

      const isPslf = state.isPslfVariant;
      const isIdr = state.planConfig.type === 'IDR';
      const calculatedPaymentForMonth = minimumMonthlyPayments[k_apply];

      let determinedQualifiedMonth: boolean;
      if (isPslf) {
        // For PSLF, actual payment must be greater than or equal to calculated payment (minus a tiny floor for float issues).
        // This correctly handles $0 calculated payments for PSLF.
        determinedQualifiedMonth = actualPayment >= calculatedPaymentForMonth - MIN_PAYMENT_FLOOR;
      } else if (isIdr) {
        // For non-PSLF IDR plans:
        // - If the calculated payment is effectively $0 (less than the floor), the month is qualified.
        // - If a payment was due (calculatedPaymentForMonth >= MIN_PAYMENT_FLOOR),
        //   then the actual payment must meet that floor.
        determinedQualifiedMonth =
          calculatedPaymentForMonth < MIN_PAYMENT_FLOOR || actualPayment >= MIN_PAYMENT_FLOOR;
      } else {
        // For other non-IDR, non-PSLF plans (e.g., Standard, Graduated, Fixed Refinance):
        // Actual payment must be at least the minimum payment floor.
        determinedQualifiedMonth = actualPayment >= MIN_PAYMENT_FLOOR;
      }

      // Always create a monthly detail for active loans
      const detail: MonthlyAmortizationDetail = {
        monthIndex: monthIndex,
        dateString: formatDateYYYYMM(addMonths(simulationStartDate, monthIndex)),
        startingPrincipal: startingPrincipalLoan,
        startingNonCapitalizedInterest: startingNonCapitalizedInterest,
        accruedInterestForMonth: accruedInterestForMonth,
        calculatedPayment: actualPayment,
        principalPayment: principalPaid,
        interestPayment: interestPaid,
        endingPrincipal: state.currentPrincipal,
        endingNonCapitalizedInterest: state.currentNonCapitalizedInterest,
        qualifiedMonth: determinedQualifiedMonth,
      };
      state.monthlyDetails.push(detail);

      if (detail.qualifiedMonth) {
        state.qualifiedMonthsCount++;
      }

      if (
        state.currentPrincipal <= MIN_PAYMENT_FLOOR &&
        state.currentNonCapitalizedInterest <= MIN_PAYMENT_FLOOR
      ) {
        state.isLoanActive = false;
        state.loanPaidOffFlag = true;
        state.payOffMonthIndex = monthIndex;
        detail.endingPrincipal = 0;
        detail.endingNonCapitalizedInterest = 0;
        if (actualPayment > totalOwedThisMonthForLoan) {
          const overpayment = actualPayment - totalOwedThisMonthForLoan;
          detail.principalPayment -= overpayment;
          if (detail.principalPayment < 0) {
            detail.interestPayment += detail.principalPayment;
            detail.principalPayment = 0;
          }
        }
      }

      if (state.isLoanActive && state.qualifiedMonthsCount >= state.forgivenessThresholdMonths) {
        state.principalAtForgiveness = state.currentPrincipal;
        state.interestAtForgiveness = state.currentNonCapitalizedInterest;
        state.currentPrincipal = 0;
        state.currentNonCapitalizedInterest = 0;
        state.isLoanActive = false;
        state.loanForgivenFlag = true;
        state.forgivenessMonthIndex = monthIndex;
        detail.endingPrincipal = 0;
        detail.endingNonCapitalizedInterest = 0;
      }
    }
    monthIndex++;
  }

  // --- III. Transform to Output (LoanResults[]) ---
  const results: LoanResults[] = loanSimulationStates.map((state) => {
    let payOffDate: Date | null = null;
    if (state.payOffMonthIndex !== null) {
      payOffDate = addMonths(simulationStartDate, state.payOffMonthIndex);
    }
    let forgivenessDate: Date | null = null;
    if (state.forgivenessMonthIndex !== null) {
      forgivenessDate = addMonths(simulationStartDate, state.forgivenessMonthIndex);
    }

    // Calculate finish date based on either payoff or forgiveness
    const finishDate = payOffDate || forgivenessDate || null;

    // Ensure we have monthly details for every month the loan was active
    const monthlyDetails =
      state.monthlyDetails.length > 0
        ? state.monthlyDetails
        : Array.from({ length: monthIndex }, (_, i) => ({
            monthIndex: i,
            dateString: formatDateYYYYMM(addMonths(simulationStartDate, i)),
            startingPrincipal: state.currentPrincipal,
            startingNonCapitalizedInterest: state.currentNonCapitalizedInterest,
            accruedInterestForMonth: 0,
            calculatedPayment: 0,
            principalPayment: 0,
            interestPayment: 0,
            endingPrincipal: state.currentPrincipal,
            endingNonCapitalizedInterest: state.currentNonCapitalizedInterest,
            qualifiedMonth: true,
          }));

    const totalLoanPaymentsRaw = monthlyDetails.reduce(
      (sum, detail) => sum + detail.calculatedPayment,
      0
    );

    // Calculate discounted payments using inflation rate
    const totalLoanPaymentsDiscounted = monthlyDetails.reduce((sum, detail) => {
      const discountFactor = Math.pow(
        1 + scenarioConfig.inflationRate,
        -(detail.monthIndex + 1) / MONTHS_PER_YEAR
      );
      return sum + detail.calculatedPayment * discountFactor;
    }, 0);

    // Calculate taxes on forgiven amount
    const taxesOwedOnForgivenRaw =
      state.loanForgivenFlag && !state.isPslfVariant
        ? (state.principalAtForgiveness + state.interestAtForgiveness) * taxRate
        : 0;

    let maxMonthPayment = 0;
    if (monthlyDetails.length > 0) {
      maxMonthPayment = monthlyDetails.reduce((max, d) => Math.max(max, d.calculatedPayment), 0);
    }

    return {
      planName: state.planType,
      loanPaidOff: state.loanPaidOffFlag,
      payOffDate: payOffDate,
      forgivenessOccurred: state.loanForgivenFlag,
      forgivenessDate: forgivenessDate,
      finishDate: finishDate,
      finalMonthIndex:
        monthlyDetails.length > 0
          ? monthlyDetails[monthlyDetails.length - 1].monthIndex
          : monthIndex - 1,
      finalPrincipal: state.currentPrincipal,
      totalLoanPaymentsRaw: totalLoanPaymentsRaw,
      totalLoanPaymentsDiscounted: totalLoanPaymentsDiscounted,
      totalForgiven: state.loanForgivenFlag
        ? state.principalAtForgiveness + state.interestAtForgiveness
        : 0,
      taxesOwedOnForgivenRaw: taxesOwedOnForgivenRaw,
      firstMonthPayment: monthlyDetails.length > 0 ? monthlyDetails[0].calculatedPayment : null,
      maxMonthPayment: maxMonthPayment,
      monthlyDetails: monthlyDetails,
    };
  });

  return results;
}
